/**
 * `scenario_picker` renderer — composes `@nextday/funnel-ui` `ScenarioPicker`.
 *
 * Stores the chosen scenario id under `data.answerKey`. Auto-advances by
 * default after the funnel-ui internal selection feedback (~280ms).
 *
 * Step JSON shape (plan §5 step 4):
 *
 *   {
 *     "type": "scenario_picker",
 *     "data": {
 *       "question": "Choose a first conversation setting.",
 *       "description": "...",
 *       "columns": 2,
 *       "autoAdvance": true,
 *       "answerKey": "scenario_id",
 *       "scenarios": [
 *         { "id": "movie_night", "label": "Cozy movie night", "description": "...", "imageUrl": "..." },
 *         ...
 *       ]
 *     }
 *   }
 */
import { ScenarioPicker } from "@nextday/funnel-ui";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";
import { urlOrTemplate } from "@survey-kit/urlOrTemplate";

const scenarioSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  description: z.string().optional(),
  emoji: z.string().optional(),
  moodEmoji: z.string().optional(),
  imageUrl: urlOrTemplate.optional(),
});

export const scenarioPickerSchema = z
  .object({
    question: z.string().min(1),
    description: z.string().optional(),
    scenarios: z.array(scenarioSchema).min(2),
    /** State key under which the chosen scenario id is recorded. */
    answerKey: z.string().min(1),
    columns: z.union([z.literal(2), z.literal(3)]).optional(),
    autoAdvance: z.boolean().optional(),
  })
  .strict();

export type ScenarioPickerData = z.infer<typeof scenarioPickerSchema>;

const ScenarioPickerRenderer = ({
  data,
  step,
  answers,
  onAnswer,
  onAdvance,
}: RendererProps<ScenarioPickerData>) => {
  const initial =
    typeof answers[data.answerKey] === "string" ? (answers[data.answerKey] as string) : undefined;
  const autoAdvance = data.autoAdvance ?? true;

  const handleAnswer = (scenarioId: string): void => {
    onAnswer(data.answerKey, { value: scenarioId });
    if (autoAdvance) onAdvance();
  };

  return (
    <ScenarioPicker
      question={data.question}
      description={data.description}
      scenarios={data.scenarios}
      initialValue={initial}
      columns={data.columns}
      autoAdvance={autoAdvance}
      onAnswer={handleAnswer}
      testId={`renderer-scenario-picker-${step.id}`}
    />
  );
};

export const scenarioPickerRenderer = createRenderer({
  type: "scenario_picker",
  dataSchema: scenarioPickerSchema,
  label: "Scenario picker",
  Component: ScenarioPickerRenderer,
});

export default scenarioPickerRenderer;
